import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Input, MenuItem, Paper, Select, Slider, Stack, TextField, Typography } from "@mui/material";
import mapboxgl, { LngLatLike, Map, MapMouseEvent, Marker } from "mapbox-gl";
import { MapType } from "../../../hooks/map.type";
import { useCallback, useEffect, useRef, useState } from "react";
import OverlaySetting from "../../../components/RobotMap/models/OverlaySetting";
import { calculateRotatedCoordinates, calculateScaledCoordinates, getCoordinates, loadOverlay, removeOverlay } from "../../../components/RobotMap/helpers/map";
import { bbox, bboxPolygon, points } from "@turf/turf";
import { createRoot } from "react-dom/client";
import { VIEW } from "../../../utils/breakpoint";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { COLOR } from "../../../utils/colors";
import { CheckCircle, Lock, LockOpen, MyLocation, NorthEastRounded, RotateLeft, RotateRight, SouthWestRounded, VisibilityOffRounded, VisibilityRounded } from "@mui/icons-material";
import { skipStyle, smallStyle } from "../../../components/RobotMap/_style";
import PinPointMarker from "../../../components/RobotMap/_components/marker/pin_point";
import { mockURL } from "../../../utils/mock";
import { smoothTransition } from "../../../utils/animate";
import { getMapLabel } from "../../scheduledJob/_function/common";
import { shadow } from "../../../utils/styles";
import { OVERLAY_APPEND_KEY } from "../../../components/RobotMap/constants";
import { Coordinates } from "../../../components/RobotMap/RobotMap.type";
import { useCreateBindMapMutation, useGetConfigBindMap } from "../../../hooks/config-page/bind-map";
import { toDegree, toRadian } from "../../../utils/utils";
import { is } from "date-fns/locale";
import { CreateBindMapProps } from "../../../hooks/config-page/bind-map.type";
import { on } from "events";

type PinPointMapType = {
  name?: string,
  coordinates: LngLatLike,
  x: number,
  y: number
}

type MapPointDataType = {
  uuid: string,
  points: PinPointMapType[]
  isEditing: boolean,
}

const LIMIT_POINT = 4
const ROBOT_KEY = "_ROBOT"
const currentRobotLayerName = 'currentRobot'
const moreControlButtonStyle = { flex: 'none', minWidth: '36px', color: COLOR.accent.blue }

export default function BindRobotMapScreen({
  onSubmit,
  onSkip,
  selectedMapsUUID = [],
  selectedRobotMapsUUID = [],
  nextLabel = 'Next',
  onBack,
  onCancel,
  showBack = false,
  showCancel = false
}: {
  onSubmit?: ({
    robotMaps,
    floorMaps,
    robotOverlaySettings
  }: {
    robotMaps: MapType[],
    floorMaps: MapType[],
    robotOverlaySettings: OverlaySetting[]
  }) => void,
  onBack?: () => void,
  onCancel?: () => void,
  showBack?: boolean,
  showCancel?: boolean,
  nextLabel?: string,
  onSkip?: () => void,
  selectedMapsUUID?: string[],
  selectedRobotMapsUUID?: string[]
}) {
  const { data: dataBindMaps } = useGetConfigBindMap({
    layout_uuids: selectedMapsUUID,
    robot_uuids: selectedRobotMapsUUID
  })

  const listRobotMaps: MapType[] = dataBindMaps?.robot_maps || []
  const listFloorMaps: MapType[] = dataBindMaps?.layout_maps || []
  const [robotOverlaySettings, setRobotOverlaySettings] = useState<OverlaySetting[]>([])
  const [mapOverlayOpacity, setMapOverlayOpacity] = useState(75)

  const [mapPinPoints, setMapPinPoints] = useState<MapPointDataType[]>((selectedRobotMapsUUID || []).map((map_uuid) => ({ uuid: map_uuid, isEditing: true, points: [] })))

  const [selectedRobotMapIndex, setSelectedRobotMapIndex] = useState(0)
  const [selectedMapIndex, setSelectedMapIndex] = useState(0)
  const [showWarningDialog, setShowWarningDialog] = useState(false)
  const [showWarningMovingDialog, setShowWarningMovingDialog] = useState(false)

  const [selectedMapId, setSelectedMapId] = useState('')
  const [selectedRobotMapId, setSelectedRobotMapId] = useState('')

  const [editableRobotMap, setEditableRobotMap] = useState(false)

  const currentOverlay = robotOverlaySettings.find((item) => item.uuid === selectedRobotMapId)

  const selectedRobotMapIdRef = useRef(selectedRobotMapId)
  selectedRobotMapIdRef.current = selectedRobotMapId

  const selectedMapIdRef = useRef(selectedMapId)
  selectedMapIdRef.current = selectedMapId

  const robotOverlaySettingsRef = useRef(robotOverlaySettings)
  robotOverlaySettingsRef.current = robotOverlaySettings

  const handleSubmit = () => {
    const checkedLists = mapPinPoints.filter((item, index) => isFullForm(index)).length === mapPinPoints.length
    if (!checkedLists) {
      setShowWarningDialog(true)
      return
    }
    onHandleSubmit()
  }

  const { trigger: submitBindMap } = useCreateBindMapMutation({
    onSuccess: () => {
      onSubmit?.({
        robotMaps: listRobotMaps,
        floorMaps: listFloorMaps,
        robotOverlaySettings
      })
    }
  })

  const mapContainer = useRef<HTMLElement | string>("");
  const map = useRef<Map | null>(null);
  const [zoomLevel, setZoomLevel] = useState(18)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const rotateIntervalRef = useRef<NodeJS.Timer | null>(null);
  const opacityIntervalRef = useRef<NodeJS.Timer | null>(null);

  const initRobotPinPoints = () => {
    if (mapPinPoints.length > 0) {
      return
    }

    const initialData: MapPointDataType[] = listRobotMaps.map((item) => ({ uuid: item.map_uuid, isEditing: true, points: [] }))
    setMapPinPoints(initialData)

    return initialData
  }

  const initRobotPinPointsRef = useRef(initRobotPinPoints)
  initRobotPinPointsRef.current = initRobotPinPoints

  const loadMaps = () => {
    if (isMapLoaded) {
      return
    }
    if (!map || !map.current || isMapLoaded || !mapContainer.current || listFloorMaps.length === 0) {
      return
    }
    listFloorMaps.forEach((item, index) => {
      const degreeRotation = toDegree(item?.map_orientation || 0)

      const mapOverlayData: OverlaySetting = {
        uuid: item.map_uuid,
        url: mockURL(item.map_image),
        topLeftLng: item.map_longitude_tl,
        topLeftLat: item.map_latitude_tl,
        widthInGIS: Math.abs(item.map_longitude_br - item.map_longitude_tl),
        heightInGIS: Math.abs(item.map_latitude_tl - item.map_latitude_br),
        opacity: mapOverlayOpacity,
        level: item.map_level,
        rotation: degreeRotation
      };

      loadOverlay(map, mapOverlayData, item.map_uuid)

      if (index > 0) {
        map.current?.setLayoutProperty(item.map_uuid, "visibility", "none");
      }
    })
    const tmpOverlays: OverlaySetting[] = []
    listRobotMaps.forEach((item, index) => {
      const degreeRotation = toDegree(item?.map_orientation || 0)

      const mapOverlayData: OverlaySetting = {
        uuid: item.map_uuid,
        // TODO: Need update from backend
        url: mockURL(item.map_image),
        topLeftLng: item.map_longitude_tl,
        topLeftLat: item.map_latitude_tl,
        widthInGIS: Math.abs(item.map_longitude_br - item.map_longitude_tl),
        heightInGIS: Math.abs(item.map_latitude_tl - item.map_latitude_br),
        opacity: 100,
        level: item.map_level,
        rotation: degreeRotation
      };
      loadOverlay(map, mapOverlayData, item.map_uuid + ROBOT_KEY)
      // Add hidden Overlay
      addMapOverlay(item.map_uuid + ROBOT_KEY, mapOverlayData)
      if (index > 0) {
        map.current?.setLayoutProperty(item.map_uuid + ROBOT_KEY, "visibility", "none");
        map.current?.setLayoutProperty(item.map_uuid + ROBOT_KEY + OVERLAY_APPEND_KEY, "visibility", "none");
      }
      tmpOverlays.push(mapOverlayData)
    })
    setRobotOverlaySettings(tmpOverlays)
    setSelectedMapId(listFloorMaps[0].map_uuid)
    setSelectedRobotMapId(listRobotMaps[0].map_uuid)
    setSelectedRobotMapIndex(0)
    initRobotPinPointsRef.current()
    setIsMapLoaded(true)
  }

  const addMapOverlay = (name: string, overlaySetting: OverlaySetting) => {
    if (!map.current) {
      return
    }
    const mapboxMap = map.current
    const currentCoordinates = getCoordinates(overlaySetting.topLeftLng, overlaySetting.topLeftLat, overlaySetting.widthInGIS, overlaySetting.heightInGIS)

    if (!overlaySetting.coordinates || overlaySetting.coordinates.length === 0) {
      overlaySetting.coordinates = currentCoordinates
    }

    const geometryData =
    {
      type: 'geojson' as 'geojson',
      data: {
        properties: {},
        type: 'Feature' as 'Feature',
        geometry: {
          type: 'Polygon' as 'Polygon',
          coordinates: [overlaySetting.coordinates]
        }
      }
    }

    mapboxMap.addSource(name + OVERLAY_APPEND_KEY, geometryData)
    mapboxMap.addLayer({
      id: name + OVERLAY_APPEND_KEY,
      type: 'fill',
      source: name + OVERLAY_APPEND_KEY,
      paint: {
        "fill-color": '#1976d2',
        "fill-opacity": 5 / 100,
      }
    })

    mapboxMap.on("mousedown", name + OVERLAY_APPEND_KEY, (e) => {
      if (!editableRef.current) {
        return
      }
      overlaySetting.dragging = true
      overlaySetting.startCoordinates = [e.lngLat.lng, e.lngLat.lat]
      mapboxMap.dragPan.disable()
      mapboxMap.doubleClickZoom.disable();
      e.preventDefault()
    });

    mapboxMap.on("mouseenter", name + OVERLAY_APPEND_KEY, (e) => {
      if (!editableRef.current) {
        return
      }
      if (!overlaySetting.dragging) {
        mapboxMap.getCanvas().style.cursor = 'pointer';
      }
    })

    mapboxMap.on("mouseleave", name + OVERLAY_APPEND_KEY, () => {
      mapboxMap.getCanvas().style.cursor = '';
    })

    mapboxMap.on("mousemove", (e) => {
      if (!editableRef.current) {
        return
      }
      mapboxMap.doubleClickZoom.disable();
      if (overlaySetting.dragging && overlaySetting.startCoordinates && overlaySetting.coordinates && overlaySetting.coordinates.length > 0) {
        e.preventDefault()

        overlaySetting.isMoved = true

        // const coords = e.lngLat;
        mapboxMap.getCanvas().style.cursor = 'grabbing';

        // Count the drag distance
        const dx = (e.lngLat.lng - overlaySetting.startCoordinates[0]);
        const dy = (e.lngLat.lat - overlaySetting.startCoordinates[1]);

        // set the new coordinate after addition in (x & y) coordinate
        const newCoordinates = overlaySetting.coordinates.map(([lng, lat]) => [lng + dx, lat + dy]) as Coordinates;

        // geometryData.data.geometry.coordinates = [newCoordinates];
        (mapboxMap.getSource(name) as mapboxgl.ImageSource).setCoordinates(newCoordinates);
        (mapboxMap.getSource(name + OVERLAY_APPEND_KEY) as mapboxgl.GeoJSONSource).setData({
          type: "Feature",
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [newCoordinates],
          },
        })
      }
    })

    const onStopDragging = (lngLat?: number[]) => {
      mapboxMap.doubleClickZoom.enable();
      if (overlaySetting.dragging) {
        mapboxMap.dragPan.enable()
        overlaySetting.dragging = false
        if (lngLat && overlaySetting.startCoordinates && overlaySetting.coordinates) {
          const dx = (lngLat[0] - overlaySetting.startCoordinates[0]);
          const dy = (lngLat[1] - overlaySetting.startCoordinates[1]);
          // set the new coordinate after addition in (x & y) coordinate
          const newCoordinates = overlaySetting.coordinates.map(([lng, lat]) => [lng + dx, lat + dy]);
          overlaySetting.coordinates = newCoordinates;
        }
        overlaySetting.startCoordinates = undefined
        mapboxMap.getCanvas().style.cursor = '';
      }
    }

    mapboxMap.on("mouseup", name + OVERLAY_APPEND_KEY, (e) => {
      onStopDragging([e.lngLat.lng, e.lngLat.lat])
    })

    mapboxMap.on("click", (e) => {
      if (overlaySetting.dragging) {
        onStopDragging([e.lngLat.lng, e.lngLat.lat])
      }
    })
  }

  useEffect(() => {
    if (!map.current || !mapContainer.current || !map.current.isStyleLoaded()) {
      return
    }
    map.current?.setLayoutProperty(selectedRobotMapId + ROBOT_KEY + OVERLAY_APPEND_KEY, "visibility", (editableRobotMap) ? "visible" : 'none');
  }, [selectedRobotMapId, editableRobotMap])

  useEffect(() => {
    if (map.current || listFloorMaps.length === 0) return; // initialize map only once
    if (!mapContainer.current) return
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/hanscau/clxan4269025p01qxaxqc0ekr",
      center: [103.83484878754473, 1.2782008843142307],
      zoom: zoomLevel,
      minZoom: 10,
      maxZoom: 26,
    });
    map.current.on('style.load', () => {
      if (!listRobotMaps || listRobotMaps.length === 0 || !listFloorMaps || listFloorMaps.length === 0) {
        return
      }
      loadMaps()
    });
    map.current?.on('click', handleClickRef.current);
  }, [listFloorMaps]);

  const onHandleClickPin = (e: MapMouseEvent) => {
    if (mapPinPoints.length === 0) {
      initRobotPinPoints()
      return
    }
    let tmpData = [...mapPinPoints]
    const currentMapIndex = currentMapIndexRef.current
    if (tmpData[currentMapIndex].points.length >= LIMIT_POINT) {
      return
    }
    const currentMap = tmpData[currentMapIndex]
    if (!currentMap.isEditing) {
      return
    }
    const newData: PinPointMapType = {
      coordinates: [e.lngLat.lng, e.lngLat.lat],
      x: 0,
      y: 0,
    }
    currentMap.points.push(newData)
    tmpData[currentMapIndex] = currentMap

    setMapPinPoints(tmpData)

    // Save Robot Map & Floor Map sync
    if (!robotOverlaySettingsRef.current || robotOverlaySettingsRef.current.length === 0 || !selectedRobotMapIdRef.current || !selectedMapIdRef.current) {
      return
    }
    const findIndex = robotOverlaySettingsRef.current.findIndex((item) => item.uuid === selectedRobotMapIdRef.current)
    if (findIndex < 0) {
      return
    }
    robotOverlaySettingsRef.current[findIndex].layout_map_uuid = selectedMapIdRef.current
  }

  const handleClickRef = useRef(onHandleClickPin)
  const currentMapIndexRef = useRef(selectedRobotMapIndex)
  handleClickRef.current = onHandleClickPin // update reference with every render

  const editableRef = useRef(editableRobotMap)
  editableRef.current = editableRobotMap

  useEffect(() => {
    currentMapIndexRef.current = selectedRobotMapIndex // update reference with every render
  }, [selectedRobotMapIndex])

  const onChangeOpacity = (value: number) => {
    if (!map.current || value > 100 || value < 0) {
      return
    }

    if (!selectedRobotMapId) { return }
    setMapOverlayOpacity(value)
    map.current.setPaintProperty(selectedRobotMapId + ROBOT_KEY, 'raster-opacity', value / 100)
  }
  useEffect(() => {
    if (!selectedRobotMapId || listRobotMaps.length === 0 || !map.current) {
      return
    }
    const findIndex = listRobotMaps.findIndex((item) => item.map_uuid === selectedRobotMapId)
    if (findIndex < 0) {
      return
    }
    setSelectedRobotMapIndex(findIndex)
    const selectedRobotMap = listRobotMaps[findIndex]
    listRobotMaps.map((rMap) => {
      map.current?.setLayoutProperty(rMap.map_uuid + ROBOT_KEY, "visibility", (rMap.map_uuid === listRobotMaps[findIndex].map_uuid) ? "visible" : 'none');
      map.current?.setLayoutProperty(rMap.map_uuid + ROBOT_KEY + OVERLAY_APPEND_KEY, "visibility", (rMap.map_uuid === listRobotMaps[findIndex].map_uuid && editableRobotMap) ? "visible" : 'none');

      if (rMap.map_uuid === selectedRobotMapId) {
        map.current?.fitBounds([
          [rMap.map_longitude_tl, rMap.map_latitude_tl],
          [rMap.map_longitude_br, rMap.map_latitude_br]
        ], {
          padding: 120,
          maxZoom: 18
        });
      }
    })

    const selectedMap = listFloorMaps.find((item) => item.map_level === selectedRobotMap.map_level)
    if (!selectedMap) {
      return
    }
    setSelectedMapId(selectedMap.map_uuid || selectedMapId)
  }, [selectedRobotMapId])

  useEffect(() => {
    if (!selectedMapId || listFloorMaps.length === 0 || !map.current) {
      return
    }
    const findIndex = listFloorMaps.findIndex((item) => item.map_uuid === selectedMapIdRef.current)
    if (findIndex < 0) {
      return
    }
    setSelectedMapIndex(findIndex)
    const selectedMap = listFloorMaps[findIndex]
    listFloorMaps.map((rMap) => {
      map.current?.setLayoutProperty(rMap.map_uuid, "visibility", (rMap.map_uuid === selectedMap.map_uuid) ? "visible" : 'none');
      if (rMap.map_uuid === selectedMapId) {
        map.current?.fitBounds([
          [rMap.map_longitude_tl, rMap.map_latitude_tl],
          [rMap.map_longitude_br, rMap.map_latitude_br]
        ], {
          padding: 120,
          maxZoom: 18
        });
      }
    })
    // Save Robot Map & Floor Map sync
    if (!robotOverlaySettingsRef.current || robotOverlaySettingsRef.current.length === 0 || !selectedRobotMapIdRef.current || !selectedMapIdRef.current) {
      return
    }
    const findIndexRobotMap = robotOverlaySettingsRef.current.findIndex((item) => item.uuid === selectedRobotMapIdRef.current)
    if (findIndex < 0) {
      return
    }

    robotOverlaySettingsRef.current[findIndexRobotMap].layout_map_uuid = selectedMapId
  }, [selectedMapId])


  useEffect(() => {

    if (!map.current || mapPinPoints.length === 0 || mapPinPoints[selectedRobotMapIndex].points.length === 0) return;
    const mapboxMap = map.current as Map;
    const markers: Marker[] = [];

    mapPinPoints[selectedRobotMapIndex].points.map((item, index) => {
      const el = document.createElement("div");
      let element = undefined;
      el.classList.add("marker");
      const name = "marker-" + index
      el.classList.add(name);

      const pointProps = {
        name: `Point ${index + 1}`,
        rotation: 0
      }
      element = <PinPointMarker {...pointProps} />

      if (element) {
        createRoot(el).render(element);
      }
      const marker = new Marker(el, { draggable: true }).setLngLat(item.coordinates).addTo(mapboxMap)
      markers.push(marker);

      marker.on('dragend', (e) => {
        const tmpData = [...mapPinPoints]
        const currentPoint = tmpData[selectedRobotMapIndex].points[index]
        currentPoint.coordinates = [marker.getLngLat().lng, marker.getLngLat().lat]

        setMapPinPoints(tmpData)
      })
    })

    return () => {
      markers.forEach((marker) => marker.remove());
    }
  }, [selectedRobotMapIndex, mapPinPoints, map.current])

  const setXValue = (value: string, pointIndex: number) => {
    const tmpData = [...mapPinPoints]
    if (!tmpData[selectedRobotMapIndex] || tmpData[selectedRobotMapIndex].points.length === 0 || pointIndex >= tmpData[selectedRobotMapIndex].points.length) {
      return
    }

    tmpData[selectedRobotMapIndex].points[pointIndex].x = value as unknown as number
    setMapPinPoints(tmpData)
  }

  const setYValue = (value: string, pointIndex: number) => {
    const tmpData = [...mapPinPoints]
    if (!tmpData[selectedRobotMapIndex] || tmpData[selectedRobotMapIndex].points.length === 0 || pointIndex >= tmpData[selectedRobotMapIndex].points.length) {
      return
    }

    tmpData[selectedRobotMapIndex].points[pointIndex].y = value as unknown as number
    setMapPinPoints(tmpData)
  }

  const onSavePoints = () => {
    if (mapPinPoints[selectedRobotMapIndex].points.length === 0) {
      return
    }

    const tmpData = [...mapPinPoints]
    tmpData[selectedRobotMapIndex].isEditing = false
    setMapPinPoints(tmpData)
  }

  const onEditPoints = () => {
    const tmpData = [...mapPinPoints]
    tmpData[selectedRobotMapIndex].isEditing = true
    setMapPinPoints(tmpData)
  }

  const isFullForm = (index: number) => {
    if (mapPinPoints.length === 0 || mapPinPoints.length <= index || !mapPinPoints[index] || mapPinPoints[index].points.length === 0) {
      return false
    }

    if (mapPinPoints[index].points.length !== LIMIT_POINT) {
      return false
    }

    const filledAll = mapPinPoints[index].points.filter((item) => !!item.x && !!item.y).length === LIMIT_POINT

    if (!filledAll) {
      return false
    }

    // return !mapPinPoints[index].isEditing
    return true
  }

  const processSubmit = (updateMove = false) => {
    setShowWarningDialog(false)
    setShowWarningMovingDialog(false)

    const form: CreateBindMapProps = {
      maps: mapPinPoints.map((item, index) => {
        const coordinates = robotOverlaySettings.find((setting) => setting.uuid === item.uuid)?.coordinates || []

        const rotation = (robotOverlaySettings[index].rotation || 0)
        const negatedRotation = rotation !== 0 ? -rotation : 0

        const rotatedCoordinates = calculateRotatedCoordinates(coordinates, negatedRotation)

        const long_tl = rotatedCoordinates[0][0]
        const lat_tl = rotatedCoordinates[0][1]
        const long_br = rotatedCoordinates[2][0]
        const lat_br = rotatedCoordinates[2][1]

        const isMove = updateMove ? (robotOverlaySettings[index]?.isMoved || false) : false

        const result: CreateBindMapProps['maps'][number] = {
          is_move: isMove,
          map_uuid: item.uuid,
          points: item.points.map((point, pointIndex) => ({
            anchor_x: point.x as number,
            anchor_y: point.y as number,
            point_name: `pt_${pointIndex + 1}`,
            gis_x: (point.coordinates as number[])[0],
            gis_y: (point.coordinates as number[])[1],
            theta: 0
          }))
        }

        if (isMove) {
          result.new_coordinates = {
            map_longitude_tl: long_tl,
            map_latitude_tl: lat_tl,
            map_longitude_br: long_br,
            map_latitude_br: lat_br,
            map_orientation: toRadian(rotation),
          }
        }

        return result
      })
    }

    submitBindMap(form)    
  }

  const onHandleSubmit = () => {
    const isMoving = robotOverlaySettings.some((item) => item.isMoved)
    if (isMoving) {
      // Need to confirm
      setShowWarningMovingDialog(true)
      return
    }

    processSubmit(false)
  }

  const handleBypassSubmit = () => {
    setShowWarningDialog(false)
    onHandleSubmit()
  }

  const increaseOpacityHandler = () => {
    opacityIntervalRef.current = setInterval(() => {
      onChangeOpacity(mapOverlayOpacity + 1)
    }, 200)
  }

  const decreaseOpacityHandler = () => {
    opacityIntervalRef.current = setInterval(() => {
      onChangeOpacity(mapOverlayOpacity - 1)
    }, 200)
  }

  const removeOpacityHandler = () => {
    if (opacityIntervalRef.current) {
      clearInterval(opacityIntervalRef.current)
      opacityIntervalRef.current = null
    }
  }

  const onRotate = (degree: number) => {
    // If no map selected then stop
    if (!map.current) {
      return
    }
    if (!currentOverlay || !currentOverlay.coordinates) {
      return
    }
    const currentRotation = (currentOverlay.rotation ? currentOverlay.rotation : degree)
    currentOverlay.rotation = currentRotation + degree

    const newCoords = calculateRotatedCoordinates(currentOverlay.coordinates, degree);
    currentOverlay.coordinates = newCoords;
    (map.current.getSource(selectedRobotMapId + ROBOT_KEY) as mapboxgl.ImageSource).setCoordinates(newCoords);
    (map.current.getSource(selectedRobotMapId + ROBOT_KEY + OVERLAY_APPEND_KEY) as mapboxgl.GeoJSONSource).setData({
      type: "Feature",
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [newCoords],
      },
    });
    // setCoordinates(selectedMapName, newCoords)
  }

  const onRotateLeft = () => {
    onRotate(-1)
  }

  const onRotateRight = () => {
    onRotate(1)
  }

  const rotateLeftHandler = () => {
    rotateIntervalRef.current = setInterval(() => {
      onRotateLeft()
    }, 200);
  }

  const removeRotateLeftHandler = () => {
    if (rotateIntervalRef.current) {
      clearInterval(rotateIntervalRef.current)
      rotateIntervalRef.current = null
    }
  }

  const rotateRightHandler = () => {
    rotateIntervalRef.current = setInterval(() => {
      onRotateRight()
    }, 200);
  }

  const removeRotateRightHandler = () => {
    if (rotateIntervalRef.current) {
      clearInterval(rotateIntervalRef.current)
      rotateIntervalRef.current = null
    }
  }

  const onChangeScale = (scaleFactor: number) => {
    if (!selectedRobotMapId || !map.current) {
      return
    }

    if (!currentOverlay || !currentOverlay.coordinates) {
      return
    }
    const newCoords = calculateScaledCoordinates(currentOverlay.coordinates, scaleFactor) as Coordinates;

    currentOverlay.coordinates = newCoords;
    (map.current.getSource(selectedRobotMapId + ROBOT_KEY) as mapboxgl.ImageSource).setCoordinates(newCoords);
    (map.current.getSource(selectedRobotMapId + ROBOT_KEY + OVERLAY_APPEND_KEY) as mapboxgl.GeoJSONSource).setData({
      type: "Feature",
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [newCoords],
      },
    })
    // setCoordinates(selectedMapName, newCoords)
  };

  const onLocateRobotMap = () => {
    if (!selectedRobotMapId || !map.current) {
      return
    }

    if (!currentOverlay || !currentOverlay.coordinates) {
      return
    }
    const box = currentOverlay.coordinates
    map.current.fitBounds([
      [box[3][0], box[3][1]],
      [box[1][0], box[1][1]]
    ], {
      padding: 20,
      maxZoom: 18
    });
  }

  const onSelectMap = (level: MapType) => {
    if (!map.current) {
      return
    }
    const selectedMap = listFloorMaps.find((item) => item.map_uuid === level.map_uuid)
    if (!selectedMap) {
      return
    }
    setSelectedMapId(selectedMap.map_uuid)
  }

  const isCurrentPointValid = () => {
    if (mapPinPoints.length === 0 || mapPinPoints.length <= selectedRobotMapIndex || !mapPinPoints[selectedRobotMapIndex] || mapPinPoints[selectedRobotMapIndex].points.length === 0) {
      return false
    }
    const filledAll = mapPinPoints[selectedRobotMapIndex].points.filter((item) => !!item.x && !!item.y).length === mapPinPoints[selectedRobotMapIndex].points.length

    if (!filledAll) {
      return false
    }
    return true
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '100vw', width: '100%', mx: 'auto' }}>
        <Box sx={{ position: 'relative', width: '100%', height: '100vh' }}>
          <Box
            ref={mapContainer}
            sx={{
              position: 'absolute',
              inset: 0,
              width: "100%",
              height: "100%",
              // zIndex: 0,
            }}
          ></Box>
          <Box sx={{ position: 'absolute', gap: '6px', bottom: '28px', right: '18px', display: 'flex', justifyContent: 'end', alignItems: 'center', mt: '46px', width: 'auto', mx: 'auto' }}>
            {
              showCancel && (
                <Button
                  onClick={() => onCancel?.()}
                  variant="outlined"
                  sx={{
                    paddingInline: '36px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    backgroundColor: '#FFF'
                  }}>
                  Cancel
                </Button>
              )
            }
            {
              showBack && (
                <Button
                  onClick={() => onBack?.()}
                  variant="outlined"
                  sx={{
                    paddingInline: '36px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    backgroundColor: '#FFF'
                  }}>
                  Back
                </Button>
              )
            }
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                paddingInline: '36px',
                paddingTop: '8px',
                paddingBottom: '8px',
                backgroundColor: '#378FFE'
              }}>
              {nextLabel}
            </Button>
          </Box>
          <Box sx={(theme) => ({
            position: 'absolute',
            [theme.breakpoints.up(VIEW.PHONE)]: {
              right: '8px',
              left: '8px',
              top: 'auto',
              bottom: '86px'
            },
            [theme.breakpoints.up(VIEW.TABLET)]: {
              left: 'auto',
              right: '16px',
              top: '16px',
              bottom: 'auto'
            },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '10px'
          })}>
            <Accordion
              sx={(theme) => ({
                [theme.breakpoints.up(VIEW.PHONE)]: {
                  width: '100%'
                },
                [theme.breakpoints.up(VIEW.TABLET)]: {
                  width: '400px'
                },
              })} defaultExpanded>
              <AccordionSummary
                expandIcon={<GridExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography component="span" fontWeight={600}>Calibration Points</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {mapPinPoints && mapPinPoints.length > 0 && mapPinPoints[selectedRobotMapIndex] && (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '39vh', overflowX: 'auto', paddingBottom: '18px' }}>
                      {mapPinPoints[selectedRobotMapIndex].points.map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', }}>
                          <Typography sx={{ fontSize: '18px', fontWeight: 500, color: COLOR.black }}>Point {index + 1}</Typography>
                          <Typography sx={{ fontSize: '12px', fontStyle: 'italic', color: COLOR.neutral[800] }}>
                            {`lng, lat: ${(item.coordinates as any)[0]}, ${(item.coordinates as any)[1]}`}
                          </Typography>
                          {index === 0 && mapPinPoints[selectedRobotMapIndex].isEditing && (
                            <Typography sx={{ fontSize: '12px', mt: '8px', color: COLOR.neutral[800] }}>
                              Please enter the Robot Coordinates
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', mt: mapPinPoints[selectedRobotMapIndex].isEditing ? '8px' : '2px', flexDirection: 'row', gap: '12px', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', flex: mapPinPoints[selectedRobotMapIndex].isEditing ? 'auto' : '', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
                              <Typography sx={{ fontSize: '12px', color: COLOR.neutral[800] }}>
                                X: {mapPinPoints[selectedRobotMapIndex].isEditing ? '' : item.x}
                              </Typography>
                              {mapPinPoints[selectedRobotMapIndex].isEditing && (
                                <TextField
                                  type="number"
                                  value={item.x || ''}
                                  inputProps={{
                                    step: "0.001"
                                  }}
                                  onChange={(e) => setXValue(e.target.value, index)} sx={{ flex: 'auto' }}
                                  size="small"
                                />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', flex: mapPinPoints[selectedRobotMapIndex].isEditing ? 'auto' : '', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
                              <Typography sx={{ fontSize: '12px', color: COLOR.neutral[800] }}>
                                Y: {mapPinPoints[selectedRobotMapIndex].isEditing ? '' : item.y}
                              </Typography>
                              {mapPinPoints[selectedRobotMapIndex].isEditing && (
                                <TextField
                                  type="number"
                                  value={item.y || ''}
                                  inputProps={{
                                    step: "0.001"
                                  }}
                                  onChange={(e) => setYValue(e.target.value, index)}
                                  sx={{ flex: 'auto' }}
                                  size="small"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))}

                      {mapPinPoints[selectedRobotMapIndex].points.length === 0 && (
                        <Typography sx={{ ...smallStyle, textAlign: 'center', color: COLOR.gray[500] }}>Pin a point on the map to start calibrate</Typography>
                      )}
                    </Box>
                    {mapPinPoints[selectedRobotMapIndex].points.length > 0 && mapPinPoints[selectedRobotMapIndex].isEditing && (
                      <Box sx={{ width: '100%', mt: '8px' }}>
                        <Button
                          onClick={() => onSavePoints()}
                          variant={isCurrentPointValid() ? "contained" : "outlined"}
                          sx={{ width: '100%' }}
                        >
                          {isCurrentPointValid() ? 'SAVE' : 'SAVE (Incomplete)'}
                        </Button>
                      </Box>
                    )}

                    {!mapPinPoints[selectedRobotMapIndex].isEditing && (
                      <Box sx={{ width: '100%', mt: '8px' }}>
                        <Button onClick={() => onEditPoints()} variant="outlined" sx={{ width: '100%' }}>EDIT</Button>
                      </Box>
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
          <Box sx={{
            position: 'absolute',
            top: '16px',
            left: '16px',
          }}>
            <Box sx={{ display: 'flex', ...shadow, borderRadius: '12px', backgroundColor: 'white', padding: '12px', flexDirection: 'column', mb: '12px' }}>
              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: COLOR.neutral[900]
                }}
              >
                Bind Robot Map to Layout Map
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: COLOR.neutral[900],
                  opacity: 0.6,
                }}
              >
                Adjust the robot map to fit the layout map and add the calibration points
              </Typography>
            </Box>

          </Box>

          {listRobotMaps.length > 0 && (
            <Box sx={{
              position: 'absolute',
              top: '110px',
              left: '16px',
            }}>
              <Box
                sx={{
                  width: '250px',
                  padding: '8px',
                  backgroundColor: COLOR.white,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  boxShadow: "0px 3px 3px 0px rgba(0, 0, 0, 0.05)",
                  borderRadius: '10px'
                }}>
                {listRobotMaps.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      border: `${index === selectedRobotMapIndex ? '2px' : '1px'} solid ${index === selectedRobotMapIndex ? COLOR.accent.blue : COLOR.gray[300]}`,
                      borderRadius: '4px',
                      padding: '12px'
                    }}
                    component={'div'}
                    onClick={() => setSelectedRobotMapId(item.map_uuid)}
                  >
                    <Typography
                      sx={{
                        fontWeight: index === selectedRobotMapIndex ? 600 : 400,
                        color: index === selectedRobotMapIndex ? COLOR.accent.blue : COLOR.black,
                        flex: 'auto'
                      }}>{item.building + ' ' + item.map_name_display}</Typography>
                    {isFullForm(index) && (
                      <Box>
                        <CheckCircle color={"success"} />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Stack
            sx={(theme) => ({
              [theme.breakpoints.up(VIEW.PHONE)]: {
                right: "15px",
                bottom: "100px",
              },
              [theme.breakpoints.up(VIEW.DESKTOP)]: {
                right: "40px",
                bottom: "90px",
              },
              position: "absolute",
              zIndex: 1000,
              bgcolor: "rgba(187, 187, 187, 0.5)",
              borderRadius: "31px",
              boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              ...smoothTransition,
            })}>
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", width: "62px", bgColor: 'red', margin: "10px auto" }}>
              {listFloorMaps.map((level, index) => (
                <Typography
                  key={index}
                  sx={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: selectedMapId === level.map_uuid ? '#378FFE' : '#9e9e9e',
                    textAlign: "center",
                    mt: 1,
                    mb: 1,
                    '&:hover': {
                      cursor: "pointer",
                      color: "#378FFE"
                    }
                  }}
                  onClick={() => onSelectMap(level)}
                >
                  {getMapLabel(level.map_level)}
                </Typography>
              ))}
            </Box>
          </Stack>
        </Box>
      </Box>

      {selectedRobotMapId && (
        <Box sx={{
          position: 'absolute',
          bottom: '28px',
          insetInline: '16px',
          maxWidth: editableRobotMap ? '560px' : '218px',
          width: 'auto',
          minHeight: editableRobotMap ? '48px' : '40px',
          backgroundColor: COLOR.white,
          mx: 'auto',
          ...shadow,
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '16px',
          padding: '8px',
          justifyContent: 'center',
          userSelect: 'none',
        }}>
          {editableRobotMap && (
            <>
              <Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
                <IconButton
                  onClick={() => onChangeOpacity(mapOverlayOpacity - 1)}
                  onMouseDown={decreaseOpacityHandler}
                  onMouseUp={removeOpacityHandler}
                  onMouseLeave={removeOpacityHandler}
                >
                  <VisibilityOffRounded sx={{ opacity: 0.3 }} />
                </IconButton>
                <Slider
                  min={0}
                  max={100}
                  value={mapOverlayOpacity}
                  onChange={(_, value) => onChangeOpacity(value as number)}
                  sx={{ minWidth: '64px' }}
                />
                <IconButton
                  onMouseDown={increaseOpacityHandler}
                  onMouseUp={removeOpacityHandler}
                  onMouseLeave={removeOpacityHandler}
                  onClick={() => onChangeOpacity(mapOverlayOpacity + 1)}
                >
                  <VisibilityRounded />
                </IconButton>
              </Stack>
              <IconButton
                onMouseDown={rotateLeftHandler}
                onMouseUp={removeRotateLeftHandler}
                onMouseLeave={removeRotateLeftHandler}
                onClick={onRotateLeft}
                sx={moreControlButtonStyle}
              >
                <RotateLeft />
              </IconButton>
              <IconButton
                onMouseDown={rotateRightHandler}
                onMouseUp={removeRotateRightHandler}
                onMouseLeave={removeRotateRightHandler}
                onClick={onRotateRight}
                sx={moreControlButtonStyle}
              >
                <RotateRight />
              </IconButton>
              <IconButton
                onClick={() => onChangeScale(0.9)}
                sx={moreControlButtonStyle}
              >
                <SouthWestRounded />
              </IconButton>
              <IconButton
                onClick={() => onChangeScale(1.1)}
                sx={moreControlButtonStyle}
              >
                <NorthEastRounded />
              </IconButton>
              <IconButton
                onClick={() => onLocateRobotMap()}
                sx={moreControlButtonStyle}
              >
                <MyLocation />
              </IconButton>
            </>
          )}
          <IconButton
            onClick={() => setEditableRobotMap(!editableRobotMap)}
            sx={{ gap: '8px' }}
          >
            {!editableRobotMap ? (
              <>
                <Lock fontSize="small" />
                <Typography>Edit Robot Map Position</Typography>
              </>
            ) : (
              <>
                <LockOpen />
                <Typography>Lock</Typography>
              </>
            )}
          </IconButton>
        </Box>
      )}
      {/* {showWarningDialog && ( */}
      <Dialog
        open={showWarningDialog}
        onClose={() => setShowWarningDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Are you sure want to skip this step?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            There is some maps that need to calibrate, are you sure want to skip?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWarningDialog(false)} autoFocus>No, Cancel</Button>
          <Button onClick={() => handleBypassSubmit()}>
            Yes, Sure
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showWarningMovingDialog}
        onClose={() => setShowWarningMovingDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          We detect that you moved some robot maps
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure want to update the Robot Map positions as well?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWarningMovingDialog(false)} autoFocus>Cancel</Button>
          <Button onClick={() => processSubmit(false)} variant="outlined">
            No, Don't Update
          </Button>
          <Button onClick={() => processSubmit(true)} variant="contained">
            Yes, Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}